import type {
  HsHlodTextureTargets,
  HsHlodTranscodedTexture,
  HsHlodTranscodeRequest,
  HsHlodTranscodeResponse,
} from '../../../shared/hlod/basis-transcode.function';

/** Round-robin pool of transcode workers; each loads the transcoder on its first request. */
export class HsHlodTextureTranscoder {
  private readonly workers: Worker[] = [];
  private readonly pending: Map<
    number,
    { resolve: (texture: HsHlodTranscodedTexture) => void; reject: (reason: Error) => void }
  > = new Map();
  private nextId: number = 1;
  private cursor: number = 0;

  constructor(
    private readonly createWorker: () => Worker,
    private readonly targets: Pick<HsHlodTextureTargets, 'etc1s' | 'uastc'>,
    private readonly assetBaseUrl: string,
    private readonly workerCount: number = 2
  ) {}

  public transcode(data: Uint8Array): Promise<HsHlodTranscodedTexture> {
    if (this.workers.length < this.workerCount) {
      const worker: Worker = this.createWorker();
      worker.onmessage = (event: MessageEvent<HsHlodTranscodeResponse>) => this.settle(event.data);
      worker.onerror = (event: ErrorEvent) =>
        this.failAll(new Error(event.message !== '' ? event.message : 'HLOD transcode worker failed.'));
      this.workers.push(worker);
    }
    const id: number = this.nextId++;
    // A copy, so the payload the tileset keeps for decoded-byte accounting stays intact.
    const buffer: ArrayBuffer = data.slice().buffer;
    const request: HsHlodTranscodeRequest = {
      id,
      data: buffer,
      targets: this.targets,
      scriptUrl: new URL('basis_transcoder.js', this.assetBaseUrl).href,
      wasmUrl: new URL('basis_transcoder.wasm', this.assetBaseUrl).href,
    };
    return new Promise<HsHlodTranscodedTexture>(
      (resolve: (texture: HsHlodTranscodedTexture) => void, reject: (reason: Error) => void) => {
        this.pending.set(id, { resolve, reject });
        this.workers[this.cursor++ % this.workers.length].postMessage(request, [buffer]);
      }
    );
  }

  public dispose(): void {
    this.workers.forEach((worker: Worker) => worker.terminate());
    this.workers.length = 0;
    this.failAll(new Error('HLOD texture transcoder was disposed.'));
  }

  private settle(response: HsHlodTranscodeResponse): void {
    const request:
      | { resolve: (texture: HsHlodTranscodedTexture) => void; reject: (reason: Error) => void }
      | undefined = this.pending.get(response.id);
    this.pending.delete(response.id);
    if (response.ok) {
      request?.resolve(response.texture);
    } else {
      request?.reject(new Error(response.error));
    }
  }

  private failAll(error: Error): void {
    this.pending.forEach(({ reject }: { reject: (reason: Error) => void }) => reject(error));
    this.pending.clear();
  }
}
